export type DurationUnit = 'months' | 'days';

export type Duration = {
  unit: DurationUnit;
  count: number;
};

const DAYS_PER_MONTH = [30, 31];

/**
 * Picks the most natural unit (months or days) to express a duration given in days.
 *
 * A month is considered to be either 30 or 31 days. If the input is a positive
 * integer multiple of any value in `DAYS_PER_MONTH`, the duration is returned in
 * months; otherwise it falls back to days. The 30-day divisor is preferred over
 * 31 when both produce an integer result.
 *
 * @param {number} days - The duration in days. Non-positive values are returned as `{ unit: 'days', count: days }`.
 * @returns {Duration} The duration expressed in the most natural unit, with the matching count.
 *
 * Example usage:
 * ```typescript
 * getDurationFromDays(30);  // { unit: 'months', count: 1 }
 * getDurationFromDays(31);  // { unit: 'months', count: 1 }
 * getDurationFromDays(60);  // { unit: 'months', count: 2 }
 * getDurationFromDays(62);  // { unit: 'months', count: 2 }
 * getDurationFromDays(14);  // { unit: 'days',   count: 14 }
 * getDurationFromDays(0);   // { unit: 'days',   count: 0 }
 * ```
 */
export const getDurationFromDays = (days: number): Duration => {
  if (days > 0) {
    const divisor = DAYS_PER_MONTH.find(d => days % d === 0);
    if (divisor) return { unit: 'months', count: days / divisor };
  }
  return { unit: 'days', count: days };
};
